import { useCallback, useMemo, useState } from 'react';

/**
 * CR-7263 — the PO Lines list's column comparator, extracted so the tables
 * inside the PO Line pop-up can use it too.
 *
 * Six of them — GRN, NCR, ASN/Shipments, the per-contract price bands on
 * Pricebooks, and the workflow and appeal audit trails on Delivery Details —
 * had no sorting at all, so comparing rows meant exporting to a spreadsheet.
 * Rather than mint another inline sort, this is the list's own comparator
 * lifted out of `usePOLinesState`, with the PO-line-specific parts turned
 * into options.
 *
 * A column sorts by the text it displays. Where a cell renders a label looked
 * up from a code, a fallback chain, or two fields joined together, the table
 * passes a `computed` accessor that derives the key exactly as the cell
 * derives its text — otherwise a column can order by something the reader
 * cannot see, which looks like no sorting at all.
 *
 * Not to be confused with `useTableSort` (CR-6296), which is the Stock
 * Planning grid's spreadsheet-style sort: it opens numeric columns high→low,
 * debounces the click and has a double-click gesture for negatives-first.
 * This one keeps the list's semantics instead — ascending first, and the four
 * behaviours below, each of which exists for a reason:
 *
 *  - **Server-ordered fields are left alone.** Re-sorting a column the
 *    database already ordered risks disagreeing with it; browser collation
 *    and SQL collation differ on case, punctuation and null placement.
 *  - **Null and undefined sort last in both directions.** A blank is absent
 *    data, not a low value, so it belongs at the end whichever way the column
 *    points — deliberately not reversed by direction.
 *  - **Numeric fields are parsed, not compared as text**, or "10" sorts
 *    before "9".
 *  - **Date fields go through getTime**, or "02/03" sorts before "10/01" of
 *    the year before.
 *
 * Client-side is right for these tables: each shows the rows of a single line
 * or delivery and none is paginated, so the loaded array is the whole result.
 * Do not add `?ordering=` for them — see R-056 (the audit feed ignores it) and
 * R-057 (the pagination control miscounts).
 */

export type ColumnSortDirection = 'asc' | 'desc';

export interface ColumnSortOptions<T> {
  /** Fields compared with parseFloat rather than as strings. */
  numericFields?: readonly string[];
  /** Fields compared as timestamps. */
  dateFields?: readonly string[];
  /**
   * Fields the server has already ordered; sorting these in the browser is
   * skipped. Accepts the list's `SERVER_SORTABLE` map or a list of names.
   */
  serverSorted?: Readonly<Record<string, unknown>> | readonly string[];
  /**
   * Columns that are not plain fields on the row — a percentage derived from
   * two others, a label assembled from several. Keyed by sort field name.
   */
  computed?: Readonly<Record<string, (row: T) => unknown>>;
}

export interface UseColumnSortOptions<T> extends ColumnSortOptions<T> {
  initialField?: string | null;
  initialDirection?: ColumnSortDirection;
  /**
   * Called after a header click with the field and the direction now in
   * effect. The PO Lines list uses this to refetch server-ordered columns.
   */
  onSortChange?: (field: string, direction: ColumnSortDirection) => void;
}

export interface UseColumnSortResult<T> {
  sortedRows: T[];
  sortField: string | null;
  sortDirection: ColumnSortDirection;
  /** Click a header: same field flips direction, a new field starts ascending. */
  onSort: (field: string) => void;
}

function isServerSorted(
  field: string,
  serverSorted: ColumnSortOptions<never>['serverSorted'],
): boolean {
  if (!serverSorted) return false;
  return Array.isArray(serverSorted)
    ? serverSorted.includes(field)
    : Boolean((serverSorted as Record<string, unknown>)[field]);
}

/**
 * The comparator itself, as a pure function.
 *
 * Exposed separately because the PO Lines list owns its sort state higher up
 * than it computes its rows — its fetch closes over `sortField` — so it cannot
 * take the state from the hook without reordering the whole thing. It calls
 * this directly, which is what keeps there being ONE comparator rather than
 * the list keeping a private copy.
 *
 * @returns A new array; the input is never mutated.
 */
export function sortRows<T>(
  rows: readonly T[],
  sortField: string | null,
  sortDirection: ColumnSortDirection,
  options: ColumnSortOptions<T> = {},
): T[] {
  const { numericFields = [], dateFields = [], serverSorted, computed } = options;
  if (!sortField) return [...rows];
  if (isServerSorted(sortField, serverSorted)) return [...rows];

  const read = (row: T): unknown => {
    const derive = computed?.[sortField];
    if (derive) return derive(row);
    return (row as Record<string, unknown>)[sortField];
  };

  return [...rows].sort((a, b) => {
    const aRaw = read(a);
    const bRaw = read(b);

    // Blanks last, whichever way the column points.
    // Two blanks compare EQUAL. The list's version returned 1 for that pair
    // too, i.e. "a after b" for a pair that is also "b after a" -- not a
    // consistent comparator. It happens to be harmless in V8, whose insertion
    // path never moves an element on a non-negative result, but that is an
    // implementation detail and these tables have whole columns that are blank
    // for every row (Sched Line, with part scheduling off). Made explicit.
    const aBlank = aRaw === null || aRaw === undefined;
    const bBlank = bRaw === null || bRaw === undefined;
    if (aBlank && bBlank) return 0;
    if (aBlank) return 1;
    if (bBlank) return -1;

    let aVal: string | number = String(aRaw);
    let bVal: string | number = String(bRaw);

    if (numericFields.includes(sortField)) {
      aVal = parseFloat(String(aRaw));
      bVal = parseFloat(String(bRaw));
    }

    if (dateFields.includes(sortField)) {
      aVal = new Date(String(aRaw)).getTime();
      bVal = new Date(String(bRaw)).getTime();
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
}

/**
 * Sort state plus sorted rows for a table that owns its own sorting.
 *
 * @param rows - The full, unpaginated set of rows on screen.
 * @returns The sorted rows, the active field and direction, and the header
 *   click handler to hand to {@link SortableTh}.
 */
export function useColumnSort<T>(
  rows: readonly T[],
  options: UseColumnSortOptions<T> = {},
): UseColumnSortResult<T> {
  const {
    numericFields, dateFields, serverSorted, computed,
    initialField = null, initialDirection = 'asc', onSortChange,
  } = options;

  const [sortField, setSortField] = useState<string | null>(initialField);
  const [sortDirection, setSortDirection] = useState<ColumnSortDirection>(initialDirection);

  const onSort = useCallback((field: string) => {
    let nextDirection: ColumnSortDirection = 'asc';
    if (sortField === field) {
      nextDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(nextDirection);
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    onSortChange?.(field, nextDirection);
  }, [sortField, sortDirection, onSortChange]);

  const sortedRows = useMemo(
    () => sortRows(rows, sortField, sortDirection, { numericFields, dateFields, serverSorted, computed }),
    [rows, sortField, sortDirection, numericFields, dateFields, serverSorted, computed],
  );

  return { sortedRows, sortField, sortDirection, onSort };
}
